import { useMemo, useState } from "react";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import { ReadyDesignThumb } from "./ready-design-thumb";
import {
  ALL_DESIGNS_FILTER,
  getCurrentReadyDesign,
  getDesignChips,
  getDesignGroupName,
  getDesignSections,
  type ReadyDesignGroupSection,
} from "./ready-designs";
import type { ReadyDesign } from "./site.documents";
import { SiteHeader } from "./site-header";
import { SiteLayout, SiteLoadingCard, SiteMessageCard } from "./site-layout";
import { useMyProviderSite, useReadyDesigns } from "./site.hooks";
import { readyDesignRoute, SITE_ROUTE } from "./site.routes";
import { thumbFor, useThumbView } from "./thumb-view";
import { ThumbViewToggle } from "./thumb-view-toggle";
import { useSiteTranslations } from "./use-site-translations";

function ReadyDesignsGallery({ profileUuid }: { profileUuid: string }) {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const { data, loading, error, refetch } = useReadyDesigns(profileUuid);
  const { data: siteData } = useMyProviderSite(profileUuid);
  const [filter, setFilter] = useState(ALL_DESIGNS_FILTER);
  const [view, setView] = useThumbView();
  const designs = useMemo(() => data?.readyDesigns.designs ?? [], [data]);
  const ownGroups = useMemo(() => data?.readyDesigns.groups ?? [], [data]);
  const current = getCurrentReadyDesign(siteData?.myProviderSite);
  const chips = getDesignChips(ownGroups, designs);
  const { leading, others } = getDesignSections(ownGroups, designs, filter);
  const groupNames = t.designs.groups as Record<string, string>;

  function showGroup(group: string) {
    setFilter(group);
    window.scrollTo({ top: 0 });
  }

  function renderSection(section: ReadyDesignGroupSection<ReadyDesign>) {
    return (
      <SoftBox key={section.group} mt={3} data-testid={`ready-designs-section-${section.group}`}>
        <SoftBox display="flex" alignItems="baseline" justifyContent="space-between" mb={1.5}>
          <SoftTypography variant="h6">
            {getDesignGroupName(section.group, groupNames)}
            <SoftTypography component="span" variant="caption" color="text" ml={1}>
              {section.total}
            </SoftTypography>
          </SoftTypography>
          {filter === ALL_DESIGNS_FILTER && section.total > section.designs.length ? (
            <SoftButton
              variant="text"
              color="info"
              size="small"
              onClick={() => showGroup(section.group)}
              data-testid={`ready-designs-see-all-${section.group}`}
            >
              {interpolateTemplate(t.designs.seeAll, { count: String(section.total) })}
            </SoftButton>
          ) : null}
        </SoftBox>
        <Grid container spacing={2}>
          {section.designs.map((design) => {
            const thumb = thumbFor(design, view);
            return (
            <Grid
              item
              {...(view === "desktop" ? { xs: 12, sm: 6, md: 4 } : { xs: 4, sm: 3, md: 2 })}
              key={design.id}
            >
              <SoftBox
                role="button"
                tabIndex={0}
                aria-label={interpolateTemplate(t.designs.tileLabel, { name: design.name })}
                data-testid={`ready-design-tile-${design.id}`}
                onClick={() => navigate(readyDesignRoute(design.id))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") navigate(readyDesignRoute(design.id));
                }}
                sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
              >
                <ReadyDesignThumb
                  uri={thumb.uri}
                  desktop={thumb.view === "desktop"}
                  current={design.id === current}
                />
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  noWrap
                  display="block"
                  mt={0.5}
                >
                  {design.name}
                </SoftTypography>
              </SoftBox>
            </Grid>
            );
          })}
        </Grid>
      </SoftBox>
    );
  }

  const header = (
    <SiteHeader
      title={t.designs.title}
      backTo={SITE_ROUTE}
      action={<ThumbViewToggle view={view} onChange={setView} />}
    />
  );

  if (loading && !data) {
    return (
      <SoftBox display="flex" flexDirection="column" gap={3}>
        {header}
        <SiteLoadingCard />
      </SoftBox>
    );
  }

  if (error && !data) {
    return (
      <SoftBox display="flex" flexDirection="column" gap={3}>
        {header}
        <SiteMessageCard>
          <SoftBox display="flex" flexDirection="column" alignItems="center" gap={2}>
            {t.designs.loadFailed}
            <SoftButton
              variant="outlined"
              color="info"
              onClick={() => void refetch().catch(() => undefined)}
              data-testid="ready-designs-retry"
            >
              {t.designs.retry}
            </SoftButton>
          </SoftBox>
        </SiteMessageCard>
      </SoftBox>
    );
  }

  return (
    <SoftBox display="flex" flexDirection="column" gap={3}>
      {header}
      <Card data-testid="ready-designs-screen">
        <SoftBox p={3}>
          <SoftBox
            display="flex"
            flexWrap="wrap"
            gap={1}
            role="toolbar"
            aria-label={t.designs.groupsLabel}
          >
            {chips.map((chip) => {
              const active = chip.id === filter;
              return (
                <SoftButton
                  key={chip.id}
                  variant={active ? "gradient" : "outlined"}
                  color={active ? "dark" : "secondary"}
                  size="small"
                  aria-pressed={active}
                  onClick={() => showGroup(chip.id)}
                  data-testid={`ready-designs-chip-${chip.id}`}
                >
                  {chip.id === ALL_DESIGNS_FILTER
                    ? t.designs.all
                    : getDesignGroupName(chip.id, groupNames)}
                  <SoftBox component="span" ml={0.75} opacity={0.7}>
                    {chip.count}
                  </SoftBox>
                </SoftButton>
              );
            })}
          </SoftBox>
          <SoftBox data-testid="ready-designs-list">
            {filter === ALL_DESIGNS_FILTER ? (
              <SoftTypography variant="caption" color="text" display="block" mt={2}>
                {t.designs.hint}
              </SoftTypography>
            ) : null}
            {leading.map(renderSection)}
            {others.length > 0 ? (
              <>
                <Divider sx={{ mt: 4, mb: 0 }} />
                <SoftTypography variant="caption" color="text" display="block" mt={2}>
                  {t.designs.otherGroups}
                </SoftTypography>
                {others.map(renderSection)}
              </>
            ) : null}
          </SoftBox>
        </SoftBox>
      </Card>
    </SoftBox>
  );
}

// "Готові дизайни": the Design library, the Provider's own groups first.
export function ReadyDesignsPage() {
  return (
    <SiteLayout>
      {({ profileUuid }) => <ReadyDesignsGallery profileUuid={profileUuid} />}
    </SiteLayout>
  );
}
